import React, { useState, useEffect } from "react";
import feather from "feather-icons";
import { ethers } from "ethers";
import { useNavigate } from "react-router-dom";

const SubmitProposal = () => {
  const [wallet, setWallet] = useState(null);
  const [form, setForm] = useState({
    name: "",
    category: "",
    description: "",
    budget: "",
    timeline: "",
    team: "",
    links: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => feather.replace(), 200);
  }, []);

  // === Connect Wallet ===
  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask or another Web3 provider!");
      return;
    }
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const address = accounts[0];
      setWallet(address);
      localStorage.setItem("wallet-address", address);
    } catch (err) {
      console.error("Wallet connection failed:", err);
    }
  };

  // === Handle Change ===
  const handleChange = (e) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  // === Handle Submit ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    if (!wallet) {
      setError("Please connect your wallet first");
      setLoading(false);
      return;
    }

    try {
      // Auth token check
      let token = localStorage.getItem("NEXORA-token");
      if (!token) {
        const res = await fetch("/api/auth/wallet", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ walletAddress: wallet }),
        });
        if (!res.ok) throw new Error("Authentication failed");
        const data = await res.json();
        token = data.token;
        localStorage.setItem("NEXORA-token", token);
      }

      const payload = {
        name: form.name,
        category: form.category,
        description: form.description,
        budget: parseFloat(form.budget),
        timeline: parseInt(form.timeline),
        team: form.team.split(",").map((t) => t.trim()).filter(Boolean),
        links: form.links.split(",").map((l) => l.trim()).filter(Boolean),
      };

      const response = await fetch("/api/proposals/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Submission failed");
      }

      setSuccess("✅ Proposal submitted successfully! It will be reviewed by the community.");
      setForm({
        name: "",
        category: "",
        description: "",
        budget: "",
        timeline: "",
        team: "",
        links: "",
      });

      setTimeout(() => navigate("/proposals"), 3000);
    } catch (err) {
      console.error("Submission error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-white min-h-screen container mx-auto px-4 py-16 max-w-4xl">
      {/* === Header === */}
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-neonPink to-neonBlue">
          Submit a Proposal
        </h1>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Share your idea and help shape the future of the NEXORA ecosystem.  
          Connect your wallet to submit your project for community review.
        </p>
        <button
          onClick={connectWallet}
          disabled={!!wallet}
          className={`mt-6 px-6 py-3 rounded-lg font-medium ${
            wallet
              ? "bg-gray-700 cursor-not-allowed"
              : "bg-gradient-to-r from-neonPurple to-neonPink"
          }`}
        >
          {wallet
            ? `${wallet.substring(0, 6)}...${wallet.slice(-4)}`
            : "Connect Wallet"}
        </button>
      </div>

      {/* === Form === */}
      <div className="bg-gray-900/80 rounded-xl p-8 border border-neonPurple/40 shadow-lg shadow-neonPurple/10 backdrop-blur-lg">
        {error && (
          <div className="p-4 mb-4 bg-red-900/20 border border-red-400 text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 mb-4 bg-green-900/20 border border-green-400 text-green-300 rounded-lg text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Project Name</label>
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              id="category"
              value={form.category}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
              required
            >
              <option value="">Select a category</option>
              <option value="education">Education</option>
              <option value="culture">Culture</option>
              <option value="health">Health</option>
              <option value="civic">Civic Engagement</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              id="description"
              rows="6"
              value={form.description}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
              required
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Requested Budget (in NEXORA)
            </label>
            <input
              id="budget"
              type="number"
              min="1000"
              value={form.budget}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Estimated Timeline (months)
            </label>
            <input
              id="timeline"
              type="number"
              min="1"
              max="24"
              value={form.timeline}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Team Members (comma separated)
            </label>
            <input
              id="team"
              type="text"
              value={form.team}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Relevant Links (comma separated)
            </label>
            <input
              id="links"
              type="text"
              value={form.links}
              onChange={handleChange}
              className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-neonPurple to-neonPink rounded-lg font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50"
          >
            {loading ? "Submitting..." : "Submit Proposal"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SubmitProposal;
